import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useStudioUser } from "../../hooks/useStudioUser";
import { useStudioPlan } from "../../hooks/useStudioPlan";
import UpgradePrompt from "../UpgradePrompt";
import {
  SubAccount,
  Permission,
  SubAccountWithPermissions,
} from "../../types/supabase";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import Notification from "../Notification";
import PasswordStrengthIndicator from "../PasswordStrengthIndicator";
import {
  isPasswordValid,
  validatePassword,
} from "../../utils/passwordValidation";

// Available page permissions – must match Sidebar navigation so all pages can be assigned to sub-accounts
const AVAILABLE_PERMISSIONS = [
  {
    name: "dashboard",
    displayName: "Dashboard",
    category: "Core",
    description: "View dashboard and analytics",
  },
  {
    name: "students",
    displayName: "Students",
    category: "Core",
    description: "View and manage students",
  },
  {
    name: "staff",
    displayName: "Staff",
    category: "Core",
    description: "View and manage staff",
  },
  {
    name: "classes",
    displayName: "Classes & Events",
    category: "Scheduling",
    description: "View and manage classes and events",
  },
  {
    name: "attendance",
    displayName: "Attendance",
    category: "Core",
    description: "View and manage attendance",
  },
  {
    name: "messaging",
    displayName: "Messaging",
    category: "Communication",
    description: "Email, Compose SMS, SMS message history, and push notifications",
  },
  {
    name: "leads",
    displayName: "Lead Management",
    category: "Sales",
    description: "View and manage leads",
  },
  {
    name: "forms",
    displayName: "Website Forms",
    category: "Marketing",
    description: "View and manage forms",
  },
  {
    name: "waivers",
    displayName: "Waivers",
    category: "Compliance",
    description: "View and manage digital waivers",
  },
  {
    name: "store",
    displayName: "Store",
    category: "Sales",
    description: "Manage store and products",
  },
  {
    name: "settings",
    displayName: "Settings",
    category: "Admin",
    description: "Access system settings",
  },
];

interface SubAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (account: any) => void;
  editingAccount?: SubAccountWithPermissions;
}

function SubAccountModal({
  isOpen,
  onClose,
  onSave,
  editingAccount,
  permissions,
}: SubAccountModalProps & { permissions: Permission[] }) {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    displayName: "",
    permissions: permissions.map((p) => ({ ...p, granted: false })),
  });
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);

  useEffect(() => {
    if (editingAccount) {
      setFormData({
        username: editingAccount.username,
        password: "", // Don't show existing password
        displayName: editingAccount.display_name,
        permissions: permissions.map((p) => ({
          ...p,
          granted:
            editingAccount.permissions?.some((ep) => ep.name === p.name) ||
            false,
        })),
      });
    } else {
      setFormData({
        username: "",
        password: "",
        displayName: "",
        permissions: permissions.map((p) => ({
          ...p,
          granted: false,
        })),
      });
    }

    // Reset password validation state
    setPasswordTouched(false);
    setShowPasswordRequirements(false);
  }, [editingAccount, isOpen, permissions]);

  const handlePermissionToggle = (permissionName: string) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.map((p) =>
        p.name === permissionName ? { ...p, granted: !p.granted } : p
      ),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password for new accounts or when updating password
    if ((!editingAccount || (editingAccount && formData.password)) && !isPasswordValid(formData.password)) {
      setPasswordTouched(true);
      setShowPasswordRequirements(true);
      return;
    }

    onSave({
      ...formData,
      id: editingAccount?.id,
    });
  };

  if (!isOpen) return null;

  // Filter out settings permission from available options
  const filteredPermissions = permissions.filter(
    (permission) => permission.name !== "settings"
  );

  const permissionsByCategory = filteredPermissions.reduce(
    (acc, permission) => {
      if (!acc[permission.category]) {
        acc[permission.category] = [];
      }
      acc[permission.category].push(permission);
      return acc;
    },
    {} as Record<string, Permission[]>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {editingAccount ? "Edit Sub-Account" : "Create Sub-Account"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6 pointer-events-none" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Username *
              </label>
              <input
                type="text"
                required
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                value={formData.username}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, username: e.target.value }))
                }
                disabled={!!editingAccount} // Can't change username after creation
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Display Name *
              </label>
              <input
                type="text"
                required
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                value={formData.displayName}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    displayName: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password {!editingAccount && "*"}
              </label>
              <input
                type="password"
                required={!editingAccount}
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                  passwordTouched && !isPasswordValid(formData.password)
                    ? "border-red-300 dark:border-red-500 focus:ring-red-500 dark:focus:ring-red-400 focus:border-red-500 dark:focus:border-red-500"
                    : "border-gray-300 dark:border-gray-600 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400"
                }`}
                value={formData.password}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    password: e.target.value,
                  }));
                  setPasswordTouched(true);
                  setShowPasswordRequirements(true);
                }}
                onFocus={() => setShowPasswordRequirements(true)}
                placeholder={editingAccount ? "Leave blank to keep current password" : "Enter a strong password"}
              />

              {/* Password validation error */}
              {passwordTouched && !isPasswordValid(formData.password) && (
                <div className="mt-2 flex items-center text-sm text-red-600 dark:text-red-400">
                  <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                  Password does not meet security requirements
                </div>
              )}
            </div>

            {/* Password Requirements Indicator */}
            {showPasswordRequirements && formData.password && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                <PasswordStrengthIndicator
                  password={formData.password}
                  showRequirements={true}
                />
              </div>
            )}

            {/* Security Tips */}
            {!passwordTouched && !editingAccount && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Security Best Practices:</strong>
                </p>
                <ul className="mt-1 text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                  <li>
                    Use a unique password that hasn't been used elsewhere
                  </li>
                  <li>Consider using a password manager</li>
                  <li>
                    Staff members should change this password after first
                    login
                  </li>
                </ul>
              </div>
            )}

            {/* Password update notice for editing */}
            {editingAccount && !passwordTouched && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Password Update:</strong> Leave the password field blank to keep the current password, or enter a new password to update it.
                </p>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Page Permissions
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                Select the pages this user can access
              </p>
            </div>
            <div className="space-y-4">
              {Object.entries(permissionsByCategory).map(
                ([category, permissions]) => (
                  <div
                    key={category}
                    className="border border-gray-200 dark:border-gray-600 rounded-lg p-4"
                  >
                    <h5 className="font-medium text-gray-900 dark:text-white mb-3">
                      {category}
                    </h5>
                    <div className="space-y-2">
                      {permissions.map((permission) => {
                        const isGranted =
                          formData.permissions.find(
                            (p) => p.name === permission.name
                          )?.granted || false;
                        return (
                          <label
                            key={permission.name}
                            className="flex items-start space-x-3 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isGranted}
                              onChange={() =>
                                handlePermissionToggle(permission.name)
                              }
                              className="mt-1 h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {permission.display_name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {permission.description}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>

          <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            {/* Password validation warning */}
            {!editingAccount &&
              formData.password &&
              !isPasswordValid(formData.password) && (
                <div className="flex items-center text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
                  <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                  Please fix password requirements before creating the account
                </div>
              )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                <span className="pointer-events-none">Cancel</span>
              </button>
              <button
                type="submit"
                disabled={
                  !editingAccount &&
                  !!formData.password &&
                  !isPasswordValid(formData.password)
                }
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  !editingAccount &&
                  formData.password &&
                  !isPasswordValid(formData.password)
                    ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 dark:bg-indigo-500 text-white hover:bg-indigo-700 dark:hover:bg-indigo-600"
                }`}
              >
                <span className="pointer-events-none">{editingAccount ? "Update" : "Create"} Sub-Account</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SubAccountManagement() {
  const { session } = useAuth();
  const { studioUser } = useStudioUser();
  const { plan, loading: planLoading } = useStudioPlan();
  const maxSubAccounts = plan?.capabilities?.maxSubAccounts ?? null;
  const [subAccounts, setSubAccounts] = useState<SubAccountWithPermissions[]>(
    []
  );
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<
    SubAccountWithPermissions | undefined
  >();
  const [accountToDelete, setAccountToDelete] =
    useState<SubAccountWithPermissions | null>(null);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({ show: false, message: "" });

  useEffect(() => {
    if (studioUser?.studio_id && session?.access_token) {
      fetchSubAccounts();
      fetchPermissions();
    }
  }, [studioUser?.studio_id, session?.access_token]);

  const fetchSubAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sub-accounts/${studioUser?.studio_id}`,
        {
          headers: { Authorization: `Bearer ${session?.access_token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSubAccounts(data);
      }
    } catch (error) {
      // Error handling without console logging
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/permissions`,
        {
          headers: { Authorization: `Bearer ${session?.access_token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Merge with AVAILABLE_PERMISSIONS so every Sidebar page is selectable (Store, etc.).
        // Use API data for id when present; prefer our displayName/description so UI text stays correct.
        // Set display_name from our list so "Store" shows like "Lead Management" (not description as title).
        const merged = AVAILABLE_PERMISSIONS.map((ap) => {
          const fromApi = data.find((d: Permission) => d.name === ap.name);
          const base = fromApi ? { ...fromApi, id: fromApi.id } : { ...ap, id: undefined };
          return {
            ...base,
            display_name: ap.displayName,
            displayName: ap.displayName,
            description: ap.description,
          };
        });
        setPermissions(merged);
      }
    } catch (error) {
      // Error handling without console logging
    }
  };

  const handleCreateAccount = () => {
    if (maxSubAccounts === 0) {
      setNotification({
        show: true,
        title: "Upgrade required",
        message:
          "Sub-accounts are not included on your current plan. Upgrade to add staff logins.",
      });
      return;
    }

    const accountLimit = maxSubAccounts ?? 5;
    if (subAccounts.length >= accountLimit) {
      setNotification({
        show: true,
        title: "Limit Reached",
        message: `You can only create up to ${accountLimit} sub-accounts on your plan.`,
      });
      return;
    }
    setEditingAccount(undefined);
    setShowModal(true);
  };

  const handleEditAccount = (account: SubAccountWithPermissions) => {
    setEditingAccount(account);
    setShowModal(true);
  };

  const handleSaveAccount = async (accountData: any) => {
    try {
      const url = accountData.id
        ? `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sub-accounts/${accountData.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sub-accounts`;

      const method = accountData.id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          username: accountData.username,
          password: accountData.password,
          displayName: accountData.displayName,
          studioId: studioUser?.studio_id,
          permissions: accountData.permissions
            .filter((p: any) => p.granted)
            .map((p: any) => {
              const dbPermission = permissions.find(
                (dbP) => dbP.name === p.name
              );
              const id = dbPermission?.id ?? p.id;
              return id
                ? { id, granted: true }
                : { name: p.name, granted: true };
            })
            .filter((p: any) => p.id || p.name),
        }),
      });

      if (response.ok) {
        setNotification({
          show: true,
          message: `Sub-account ${
            accountData.id ? "updated" : "created"
          } successfully!`,
        });
        setShowModal(false);
        fetchSubAccounts();
      } else {
        const error = await response.json();
        setNotification({
          show: true,
          title: "Error",
          message: error.error || "Failed to save sub-account",
        });
      }
    } catch (error) {
      // Error handling without console logging
      setNotification({
        show: true,
        title: "Error",
        message: "Failed to save sub-account",
      });
    }
  };

  const handleDeleteAccount = async (account: SubAccountWithPermissions) => {
    if (!studioUser?.studio_id) {
      setNotification({
        show: true,
        message: "No studio ID found",
        title: "Error",
      });
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sub-accounts/${account.id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete sub-account");
      }

      // Refresh sub-accounts list
      fetchSubAccounts();

      setNotification({
        show: true,
        message: "Sub-account deleted successfully!",
        title: "Success",
      });
    } catch (error) {
      // Error handling without console logging
      setNotification({
        show: true,
        title: "Error",
        message: "Failed to delete sub-account",
      });
      throw error;
    }
  };

  const confirmDelete = async () => {
    if (!accountToDelete) return;

    try {
      await handleDeleteAccount(accountToDelete);
      setAccountToDelete(null);
    } catch (err) {
      // Error handling is done in handleDeleteAccount
    }
  };

  const cancelDelete = () => {
    setAccountToDelete(null);
  };

  return (
    <div className="">
      <Notification
        show={notification.show}
        message={notification.message}
        title={notification.title}
        onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
      />

      {!planLoading && maxSubAccounts === 0 && (
        <UpgradePrompt capability="stripeBilling" title="Sub-accounts require a paid plan" description="Upgrade to add staff logins with limited permissions." />
      )}

      {!planLoading && (maxSubAccounts ?? 0) > 0 && (
      <>
      <div className="mb-4">
        <div className="mb-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {`Create up to ${maxSubAccounts ?? 5} additional accounts with limited permissions for staff or assistants.`}
          </p>
        </div>
        <button
          onClick={handleCreateAccount}
          disabled={subAccounts.length >= (maxSubAccounts ?? 5)}
          className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md transition-colors
            ${subAccounts.length >= (maxSubAccounts ?? 5)
              ? "text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-700 cursor-not-allowed"
              : "text-green-600 dark:text-green-400 bg-white dark:bg-gray-700 border-green-300 dark:border-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
            }`}
        >
          <PlusIcon className="h-4 w-4 mr-2 pointer-events-none" />
          <span className="pointer-events-none">
            Add Sub-Account {subAccounts.length}/{maxSubAccounts ?? 5}
          </span>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {subAccounts.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-gray-500 dark:text-gray-400">
                No sub-accounts created yet.
              </p>
              <button
                onClick={handleCreateAccount}
                className="mt-2 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
              >
                <span className="pointer-events-none">Create your first sub-account</span>
              </button>
            </div>
          ) : (
            subAccounts.map((account) => (
              <div
                key={account.id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                      {account.display_name}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Username: {account.username}
                    </p>
                    <div className="mt-2">
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        Permissions:{" "}
                      </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {account.permissions?.map((permission) => (
                          <span
                            key={permission.id}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200"
                          >
                            {permission.display_name}
                          </span>
                        )) || (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            No permissions assigned
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditAccount(account)}
                      className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-indigo-600 dark:text-indigo-400"
                    >
                      <PencilIcon className="h-5 w-5 pointer-events-none" />
                    </button>
                    <button
                      onClick={() => setAccountToDelete(account)}
                      className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-red-600 dark:text-red-400"
                    >
                      <TrashIcon className="h-5 w-5 pointer-events-none" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      </>
      )}

      <SubAccountModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSaveAccount}
        editingAccount={editingAccount}
        permissions={permissions}
      />

      {/* Delete Confirmation Modal */}
      {accountToDelete && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={cancelDelete}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                </button>
              </div>
              <div>
                <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                  Delete Sub-Account
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Are you sure you want to delete{" "}
                    {accountToDelete.display_name}? This action cannot be
                    undone.
                  </p>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                <button
                  type="button"
                  onClick={cancelDelete}
                  className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <span className="pointer-events-none">Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
